/**
 * Catalogo degli eventi di notifica e configurazione di quando e come inviarle (Amministrazione → Notifiche).
 * Il codice di un evento è il `kind` scritto da notify() in Notification e in MailOutbox: è stabile, non si rinomina.
 * I default riproducono ESATTAMENTE il comportamento precedente alla configurazione (stessi destinatari, nessun orario).
 */

/** Ruoli destinatario: supplier = referenti del fornitore, auditor, manager = audit manager dell'audit, owner = responsabile (owner) dell'azione o della NC, functionHead = responsabile di funzione da configurazione, admin = tutti gli admin attivi. */
export const RECIPIENT_ROLES = ["supplier", "auditor", "manager", "owner", "functionHead", "admin"] as const;
export type RecipientRole = (typeof RECIPIENT_ROLES)[number];

export const NOTIFICATION_CHANNELS = ["mail", "teams"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];
/** Canali selezionabili oggi: Teams è predisposto ma non attivo. */
export const ACTIVE_CHANNELS: readonly NotificationChannel[] = ["mail"];

export const NOTIFICATION_GROUPS = ["audit", "nc", "reminders", "account"] as const;
export type NotificationGroup = (typeof NOTIFICATION_GROUPS)[number];

export interface NotificationEventDef {
  kind: string;
  group: NotificationGroup;
  /** ruoli che il punto di invio sa risolvere (admin e functionHead li risolve sempre notify) */
  offered: RecipientRole[];
  /** destinatari di oggi per questo evento */
  defaults: RecipientRole[];
  /** acceso di default; false solo per eventi nuovi che prima non inviavano nulla */
  defaultEnabled: boolean;
  /** necessaria al funzionamento: non si disattiva, non dipende dall'interruttore globale, né dalla finestra di invio */
  locked?: boolean;
  /** sicurezza: non dipende dall'interruttore globale né dalla finestra, ma il singolo evento si può spegnere (con avviso) */
  critical?: boolean;
}

const e = (kind: string, group: NotificationGroup, offered: RecipientRole[], defaults: RecipientRole[], extra: Partial<NotificationEventDef> = {}): NotificationEventDef => ({
  kind,
  group,
  offered,
  defaults,
  defaultEnabled: true,
  ...extra,
});

export const NOTIFICATION_EVENTS = [
  // Audit
  e("audit_created", "audit", ["auditor", "manager", "supplier"], ["auditor", "supplier"]),
  e("followup_planned", "audit", ["auditor", "manager", "supplier"], ["auditor", "supplier"]),
  e("audit_updated", "audit", ["auditor", "manager", "supplier"], ["auditor", "supplier"]),
  e("audit_confirmed", "audit", ["auditor", "manager"], ["auditor", "manager"]),
  e("audit_returned", "audit", ["auditor", "manager"], ["auditor"]),
  e("audit_validation", "audit", ["auditor", "manager"], ["manager"]),
  // programma di audit inviato dal tool al fornitore con il PDF allegato (AUD-F2.14b)
  e("programme_sent", "audit", ["supplier", "auditor", "manager"], ["supplier"]),
  e("report_published", "audit", ["auditor", "manager", "supplier"], ["supplier", "auditor"]),
  e("phaseout", "audit", ["auditor", "manager"], ["manager", "auditor"]),
  // Non conformità e CAP
  e("nc_opened", "nc", ["supplier", "owner", "auditor", "manager"], ["supplier", "owner"]),
  e("action_assigned", "nc", ["supplier", "owner"], ["supplier"]),
  e("action_update", "nc", ["supplier", "owner"], ["supplier", "owner"]),
  e("evidence_uploaded", "nc", ["auditor", "owner"], ["auditor", "owner"]),
  e("evidence_submitted", "nc", ["auditor", "owner"], ["owner", "auditor"]),
  e("evidence_rejected", "nc", ["supplier"], ["supplier"]),
  e("cap_in_review", "nc", ["auditor", "manager", "supplier"], ["manager", "auditor"], { defaultEnabled: false }),
  e("followup_closed", "nc", ["supplier", "manager"], ["supplier", "manager"]),
  // Promemoria ed escalation
  e("reminder", "reminders", ["supplier", "owner"], ["supplier", "owner"]),
  e("overdue", "reminders", ["supplier", "owner"], ["supplier", "owner"]),
  e("escalation15", "reminders", ["manager"], ["manager"]),
  e("escalation30", "reminders", [], ["functionHead"]),
  // Account e sicurezza
  e("invite", "account", [], [], { locked: true }),
  e("password_reset", "account", [], [], { locked: true }),
  e("integrity_alert", "account", [], ["admin"], { critical: true }),
] as const satisfies readonly NotificationEventDef[];

export type NotificationKind = (typeof NOTIFICATION_EVENTS)[number]["kind"];
export const NOTIFICATION_KINDS = NOTIFICATION_EVENTS.map((x) => x.kind) as NotificationKind[];
export const EVENT_BY_KIND = Object.fromEntries(NOTIFICATION_EVENTS.map((x) => [x.kind, x])) as Record<NotificationKind, NotificationEventDef>;
export const isNotificationKind = (k: string): k is NotificationKind => k in EVENT_BY_KIND;
/** Ruoli selezionabili per un evento: quelli del punto di invio più admin e responsabile di funzione. */
export const rolesOffered = (kind: NotificationKind): RecipientRole[] => {
  const d = EVENT_BY_KIND[kind];
  return [...new Set<RecipientRole>([...d.offered, ...d.defaults, "functionHead", "admin"])].filter((r) => d.locked ? d.defaults.includes(r) : true);
};
/** Eventi che si spediscono anche con l'interruttore globale spento e fuori dalla finestra di invio. */
export const BYPASS_KINDS: NotificationKind[] = NOTIFICATION_EVENTS.filter((x) => (x as NotificationEventDef).locked || (x as NotificationEventDef).critical).map((x) => x.kind);

export interface NotificationEventConfig {
  enabled: boolean;
  roles: RecipientRole[];
  /** indirizzi fissi aggiuntivi */
  extra: string[];
  channels: NotificationChannel[];
}
export interface SendWindowConfig {
  /** false = nessuna restrizione di orario */
  hoursEnabled: boolean;
  from: string; // HH:MM
  to: string; // HH:MM
  timezone: string;
  /** true = sabato e domenica esclusi */
  workdaysOnly: boolean;
}
export interface ReminderRulesConfig {
  /** giorni prima della scadenza in cui parte un promemoria (0 = il giorno stesso) */
  leadDays: number[];
  /** dopo la scadenza: una notifica ogni N giorni (1 = ogni giorno) */
  overdueEveryDays: number;
  /** giorni di ritardo oltre i quali scatta l'escalation all'audit manager */
  managerAfterDays: number;
  /** giorni di ritardo oltre i quali scatta l'escalation al responsabile di funzione */
  functionHeadAfterDays: number;
}
export interface NotificationsConfig {
  /** interruttore globale (default true) */
  enabled: boolean;
  events: Record<NotificationKind, NotificationEventConfig>;
  reminders: ReminderRulesConfig;
  window: SendWindowConfig;
}

export const DEFAULT_REMINDERS: ReminderRulesConfig = { leadDays: [30, 15, 7, 0], overdueEveryDays: 1, managerAfterDays: 15, functionHeadAfterDays: 30 };
export const DEFAULT_WINDOW: SendWindowConfig = { hoursEnabled: false, from: "08:00", to: "18:00", timezone: "Europe/Rome", workdaysOnly: false };

export const defaultEventConfig = (kind: NotificationKind): NotificationEventConfig => {
  const d = EVENT_BY_KIND[kind];
  return { enabled: d.defaultEnabled, roles: [...d.defaults], extra: [], channels: ["mail"] };
};
export const defaultNotificationsConfig = (): NotificationsConfig => ({
  enabled: true,
  events: Object.fromEntries(NOTIFICATION_KINDS.map((k) => [k, defaultEventConfig(k)])) as Record<NotificationKind, NotificationEventConfig>,
  reminders: { ...DEFAULT_REMINDERS, leadDays: [...DEFAULT_REMINDERS.leadDays] },
  window: { ...DEFAULT_WINDOW },
});

type DeepPartialNotifications = {
  enabled?: boolean;
  events?: Partial<Record<string, Partial<NotificationEventConfig>>>;
  reminders?: Partial<ReminderRulesConfig>;
  window?: Partial<SendWindowConfig>;
};

/** Unisce una configurazione notifiche (anche parziale o assente) ai default, evento per evento. Gli eventi bloccati restano sempre accesi. */
export function mergeNotifications(d?: DeepPartialNotifications | null): NotificationsConfig {
  const base = defaultNotificationsConfig();
  const x = d ?? {};
  for (const k of NOTIFICATION_KINDS) {
    const o = x.events?.[k];
    if (!o) continue;
    const def = EVENT_BY_KIND[k];
    base.events[k] = {
      enabled: def.locked ? true : o.enabled ?? base.events[k].enabled,
      roles: def.locked ? [...def.defaults] : Array.isArray(o.roles) ? o.roles.filter((r): r is RecipientRole => (RECIPIENT_ROLES as readonly string[]).includes(r)) : base.events[k].roles,
      extra: def.locked ? [] : Array.isArray(o.extra) ? o.extra : [],
      channels: ["mail"],
    };
  }
  return {
    enabled: x.enabled ?? true,
    events: base.events,
    reminders: { ...base.reminders, ...(x.reminders ?? {}), leadDays: Array.isArray(x.reminders?.leadDays) ? x.reminders!.leadDays : base.reminders.leadDays },
    window: { ...base.window, ...(x.window ?? {}) },
  };
}

/** Quanti eventi sono spenti (non contano quelli che la configurazione di fabbrica tiene spenti). */
export const countDisabledEvents = (c: NotificationsConfig) => NOTIFICATION_KINDS.filter((k) => !c.events[k].enabled && EVENT_BY_KIND[k].defaultEnabled).length;

const hm = (s: string) => {
  const [h, m] = s.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};
/** Giorno della settimana (0 = domenica) e minuti dall'inizio del giorno di `date` nel fuso indicato. */
export function zonedParts(date: Date, timeZone: string): { weekday: number; minutes: number } {
  const f = new Intl.DateTimeFormat("en-GB", { timeZone, weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(date);
  const get = (t: string) => f.find((p) => p.type === t)?.value ?? "";
  const wd = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(get("weekday"));
  return { weekday: wd, minutes: Number(get("hour")) * 60 + Number(get("minute")) };
}
/** true se in questo istante è consentito spedire le mail (senza restrizioni: sempre). I festivi non sono considerati. */
export function isWithinSendWindow(w: SendWindowConfig, date: Date = new Date()): boolean {
  if (!w.hoursEnabled && !w.workdaysOnly) return true;
  let p: { weekday: number; minutes: number };
  try {
    p = zonedParts(date, w.timezone);
  } catch {
    return true; // fuso non valido: non blocca la posta
  }
  if (w.workdaysOnly && (p.weekday === 0 || p.weekday === 6)) return false;
  if (w.hoursEnabled) {
    const a = hm(w.from);
    const b = hm(w.to);
    return a <= b ? p.minutes >= a && p.minutes < b : p.minutes >= a || p.minutes < b;
  }
  return true;
}
